import type { FC } from "react";
import { ChevronDown, Check, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useAuth } from "src/contexts/AuthContext";
import type { Organization } from "src/types/OrganizationDTO";
import type { BootstrapMembership } from "src/types/BootstrapDTO";
import PlanBadge from "./PlanBadge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "src/components/ui/dropdown-menu";

interface OrgSwitcherProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const OrgSwitcher: FC<OrgSwitcherProps> = observer(
  ({ isOpen, onToggle, onClose }) => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { organizations, currentOrgSubscription, memberships } = useAuth();

    const orgList = Array.isArray(organizations) ? organizations : [];
    // diagnostic log - remove when confirmed
    console.debug("OrgSwitcher memberships:", memberships);
    const selectedOrg: Organization | null = (() => {
      try {
        return JSON.parse(
          localStorage.getItem("currentOrganization") || "null",
        );
      } catch {
        return null;
      }
    })();

    const handleOrganizationSelect = (org: Organization) => {
      const previousOrg = localStorage.getItem("currentOrganization");
      try {
        localStorage.setItem("currentOrganization", JSON.stringify(org));
        window.dispatchEvent(
          new CustomEvent("organizationChanged", {
            detail: { organization: org },
          }),
        );
        window.location.reload();
      } catch (error) {
        console.error("Failed to switch organization:", error);
        if (previousOrg) {
          localStorage.setItem("currentOrganization", previousOrg);
        }
      }
      onClose();
    };

    const handleCreateOrganization = () => {
      onClose();
      navigate("/accounts/new");
    };

    // No organizations edge case
    if (orgList.length === 0) {
      return (
        <button
          type="button"
          onClick={handleCreateOrganization}
          className="hidden md:flex items-center gap-2 px-2 py-1 rounded-md text-sm text-gray-600 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          <Plus className="h-4 w-4" />
          <span>{t("navbar.create-organization")}</span>
        </button>
      );
    }

    const currentOrgName =
      selectedOrg?.name || t("selectOrganization", { ns: "translation" });
    // determine plan of selected org via subscription or membership data
    const currentOrgPlan =
      currentOrgSubscription?.subscription?.planName ||
      memberships.find(m => m.organizationId === selectedOrg?.id)
        ?.subscription?.planName ||
      selectedOrg?.attributes?.plan?.[0];
    const currentOrgInitial = currentOrgName.charAt(0).toUpperCase();

    return (
      <DropdownMenu
        open={isOpen}
        onOpenChange={open => {
          if (!open) onClose();
          else onToggle();
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              onToggle();
            }}
            className="hidden md:flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-blue-50 to-primary-50 flex items-center justify-center border border-blue-100 flex-shrink-0">
              <span className="text-xs font-bold bg-gradient-to-r from-blue-600 to-primary-600 bg-clip-text text-transparent">
                {currentOrgInitial}
              </span>
            </div>
            <span className="text-sm font-medium text-gray-900 max-w-[140px] truncate">
              {currentOrgName}
            </span>
            <PlanBadge
              planName={currentOrgPlan}
              size="sm"
              labelMode="original"
            />
            <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[280px]">
          <div className="max-h-48 overflow-y-auto">
            {orgList.map((org: Organization) => {
              const isCurrent = selectedOrg?.id === org.id;
              // find membership for org to get its subscription/plan
              const membership = memberships.find(m => m.organizationId === org.id);
              const orgPlanName = isCurrent
                ? currentOrgSubscription?.subscription?.planName || membership?.subscription?.planName || org.attributes?.plan?.[0]
                : membership?.subscription?.planName || org.attributes?.plan?.[0];
              return (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => handleOrganizationSelect(org)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="w-4 flex-shrink-0">
                    {isCurrent && (
                      <Check className="h-4 w-4 text-primary-600" />
                    )}
                  </div>
                  <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-blue-50 to-primary-50 flex items-center justify-center border border-blue-100 flex-shrink-0">
                    <span className="text-xs font-bold bg-gradient-to-r from-blue-600 to-primary-600 bg-clip-text text-transparent">
                      {org.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm text-gray-900 truncate flex-1">
                    {org.name}
                  </span>
                  <PlanBadge
                    planName={orgPlanName}
                    size="sm"
                    labelMode="original"
                  />
                </DropdownMenuItem>
              );
            })}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleCreateOrganization}
            className="flex items-center gap-3 cursor-pointer text-primary-600"
          >
            <Plus className="h-4 w-4" />
            <span>{t("navbar.create-organization")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);

export default OrgSwitcher;
